import MessageQueue from '../../../services/MessageQueue';
import {
  COLLECT_CHANGED_BY_USER_SITE_DATA_SUCCESS,
  COLLECT_CHANGED_BY_USER_SITE_DATA
} from '../consts/events';
import PageElementsDataModel from '../model/PageElementsDataModel';
import IframeService from './abstract/IframeService';
import FrameEventMessanger from './frames/FrameEventMessanger';
import IframeServiceInjector from './injectors/IframeServiceInjector';

type ElementType =
  | 'article'
  | 'picture'
  | 'sectionGroup'
  | 'teaser'
  | 'navigationSection'
  | 'socialLinks';

export interface SiteElementData {
  areaTypeName: string;
  cmsElementId: string;
  metaInfo?: any;
  content: any;
  templateId: number;
  type: ElementType;
}

export default class PageBuilderDataCollector extends IframeService {
  private iframeRef: HTMLIFrameElement;

  private waitForDataCollectionResolve = null;

  private currentPageId: number | string;

  constructor() {
    super('PageBuilderDataCollector', true);
  }

  public injectIframe(iframe: HTMLIFrameElement): void {
    this.iframeRef = iframe;
  }

  public initEvents(): void {
    MessageQueue.off(COLLECT_CHANGED_BY_USER_SITE_DATA_SUCCESS);

    /**
     * We collect only the data, that user changed in the current page
     */
    MessageQueue.on(COLLECT_CHANGED_BY_USER_SITE_DATA_SUCCESS, (event) => {
      const { siteData } = event.data.payload as {
        siteData: Array<SiteElementData>;
      };

      // extract template elements
      siteData.forEach((data) => {
        if (data.templateId !== null) {
          PageElementsDataModel.pushTemplateElement(data.templateId, data);
        } else {
          PageElementsDataModel.pushPageElement(this.currentPageId, data);
        }
      });

      if (this.waitForDataCollectionResolve) {
        this.waitForDataCollectionResolve();
      }
    });
  }

  public async collectSiteDataForTheCurrentPage(
    currentPageId: number | string
  ): Promise<void> {
    await new Promise((resolve) => {
      this.waitForDataCollectionResolve = resolve;
      this.currentPageId = currentPageId;

      // fire event to collect site data for the current page
      IframeServiceInjector.getService<FrameEventMessanger>(
        'FrameEventMessanger'
      ).sendMessage(COLLECT_CHANGED_BY_USER_SITE_DATA, {});
    });
  }

  /**
   * Returns only the data that user filled in the current page.
   *
   * @returns
   */
  public getSiteDataChangedByTheUser() {
    return {
      pageElements: PageElementsDataModel.pageElements,
      templateElements: PageElementsDataModel.templateElements
    };
  }

  public getPageElementsForPage(pageId: number | string) {
    return PageElementsDataModel.pageElements.get(pageId);
  }

  public getFilledAreasForPageId(pageId: number | string) {
    const map = {};

    let pageElements = PageElementsDataModel.pageElements.get(pageId);

    if (pageElements) {
      pageElements.forEach((element) => {
        // filter out section and navigation
        if (
          element.areaTypeName !== void 0 &&
          element.areaTypeName !== null // for section and navigation, we don't have areaTypeName
        ) {
          if (map[element.areaTypeName] === void 0) {
            map[element.areaTypeName] = [];
          }

          map[element.areaTypeName].push({
            cmsElementId: element.cmsElementId,
            content: element.content
          });
        }
      });
    }

    return map;
  }
}
